/**
 * Trunnion AI - Mission Workflow Card Component
 * Compact draggable card representing a workflow within a mission column.
 * Displays workflow name, classification, execution status, and agent count
 * with a drag handle for reordering across mission columns.
 */

import { type DragEvent } from 'react';
import { GripVertical, Users } from 'lucide-react';
import { ClassificationTag } from '@/components/ui/ClassificationTag';
import { Badge } from '@/components/ui/Badge';
import type { ClassificationLevel } from '@/types';
import type { DragItem } from '@/hooks/useDragAndDrop';

/** Execution status options for a workflow card. */
export type WorkflowExecutionStatus =
  | 'success'
  | 'failed'
  | 'running'
  | 'pending'
  | 'never_executed';

/** Props for the MissionWorkflowCard component. */
interface MissionWorkflowCardProps {
  /** Unique workflow identifier */
  id: string;
  /** Workflow display name */
  name: string;
  /** Classification level of the workflow */
  classification: ClassificationLevel;
  /** Last execution status or "never_executed" */
  executionStatus: WorkflowExecutionStatus;
  /** Number of agents assigned to this workflow */
  agentCount: number;
  /** The mission column this card belongs to */
  missionId: string;
  /** Handler called when drag starts on this card */
  onDragStart: (e: DragEvent<HTMLElement>, item: DragItem) => void;
  /** Handler called when drag ends */
  onDragEnd: () => void;
  /** Whether this specific card is currently being dragged */
  isBeingDragged?: boolean;
}

/** Maps execution status to Badge variant for consistent styling. */
const statusVariantMap: Record<WorkflowExecutionStatus, 'success' | 'error' | 'info' | 'warning' | 'default'> = {
  success: 'success',
  failed: 'error',
  running: 'info',
  pending: 'warning',
  never_executed: 'default',
};

/** Maps execution status to human-readable label. */
const statusLabelMap: Record<WorkflowExecutionStatus, string> = {
  success: 'Success',
  failed: 'Failed',
  running: 'Running',
  pending: 'Pending',
  never_executed: 'Never executed',
};

/**
 * Compact draggable card for a workflow within a mission board column.
 *
 * Features:
 * - Drag handle (GripVertical) on the left for initiating drag operations
 * - Truncated workflow name with tooltip on overflow
 * - Classification tag matching project-wide classification system
 * - Execution status badge with color coding
 * - Agent count indicator
 * - Hover state with elevation (shadow lift)
 * - Reduced opacity when being actively dragged
 */
export function MissionWorkflowCard({
  id,
  name,
  classification,
  executionStatus,
  agentCount,
  missionId,
  onDragStart,
  onDragEnd,
  isBeingDragged = false,
}: MissionWorkflowCardProps) {
  const dragItem: DragItem = {
    id,
    type: 'workflow',
    sourceColumn: missionId,
  };

  return (
    <div
      draggable="true"
      onDragStart={(e) => onDragStart(e, dragItem)}
      onDragEnd={onDragEnd}
      className={`group flex items-start gap-2 rounded-lg border border-trunnion-slate-700 bg-trunnion-slate-800/50 p-3 transition-all cursor-grab active:cursor-grabbing hover:border-trunnion-slate-600 hover:bg-trunnion-slate-800 hover:shadow-lg hover:shadow-black/20 hover:-translate-y-0.5 ${
        isBeingDragged ? 'opacity-40 scale-95' : ''
      }`}
    >
      {/* Drag handle */}
      <div className="mt-0.5 shrink-0 text-trunnion-slate-500 group-hover:text-trunnion-slate-300 transition-colors">
        <GripVertical className="h-4 w-4" />
      </div>

      {/* Card content */}
      <div className="min-w-0 flex-1 space-y-2">
        {/* Workflow name */}
        <p
          className="text-sm font-medium text-white truncate"
          title={name}
        >
          {name}
        </p>

        {/* Classification and status row */}
        <div className="flex items-center gap-2 flex-wrap">
          <ClassificationTag level={classification} size="sm" showIcon={false} />
          <Badge
            variant={statusVariantMap[executionStatus]}
            dot={executionStatus === 'running'}
          >
            {statusLabelMap[executionStatus]}
          </Badge>
        </div>

        {/* Agent count */}
        <div className="flex items-center gap-1.5 text-xs text-trunnion-slate-400">
          <Users className="h-3 w-3" />
          <span>
            {agentCount} {agentCount === 1 ? 'agent' : 'agents'}
          </span>
        </div>
      </div>
    </div>
  );
}
